import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {ApiUi} from '@api/components'
import {renameAllowed} from '@client/hub-actions-allowed'
import {useBoardStore} from '@components/board/board-store'
import {useColumnStore} from '@components/columns/column-store'
import {
  TwTaskGroupTitleText,
  TwTextArea,
  TwTextAreaWrapper,
  TwTooltipText
} from '@components/columns/column-title/containers'
import {Badge} from '@components/common/base/badge'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {noPropagate} from '@containers/hooks'

interface Props {
  type: 'mirror' | 'basic'
}

export const EditableColumnTitle = observer(({type}: Props) => {
  const [state, store] = useColumnStore()
  const [, boardStore] = useBoardStore()
  const column = store.column
  const board = boardStore.board
  const showCounterInColumns = board?.data?.settings?.counterInColumns

  // Show tooltip only if a column title starts scrolling
  const titleRef = useRef<HTMLElement>(null)
  const [showTooltip, setShowTitleTooltip] = useState(false)
  const tooltipVisible = showTooltip && !state.groupEdit
  useEffect(() => {
    if (titleRef.current) {
      const scrollHeight = titleRef.current.scrollHeight
      const height = titleRef.current.getBoundingClientRect().height
      setShowTitleTooltip(scrollHeight > height)
    }
  }, [titleRef, setShowTitleTooltip, state.visibleColumnTitle])

  const count = (state.openTaskList.get(column.id) || []).length

  return (
    <Tooltip
      content={<TwTooltipText>{state.visibleColumnTitle}</TwTooltipText>}
      placement="bottom"
      disabled={!tooltipVisible}
    >
      {state.groupEdit ? (
        <TwTextAreaWrapper
          // w/o this mouse selection inside text moves column instead
          onMouseDown={noPropagate}
          // wheel capture is required to prevent perfect scroll from handling it first
          onWheelCapture={noPropagate}
        >
          <TwTextArea
            placeholder={Lang.msg.columnPlaceholder}
            defaultValue={state.visibleColumnTitle}
            onSubmit={store.groupEditSubmit}
            onBlur={store.groupEditSubmit}
            onCancel={store.groupEditCancel}
            autoFocus
            borders={false}
            paddings="none"
            noBackground
            style={{fontWeight: '600', lineHeight: '24px', fontSize: '16px'}}
            color="primary"
            textAreaClassName="max-h-200 !overflow-y-auto"
          />
        </TwTextAreaWrapper>
      ) : (
        <TwTaskGroupTitleText
          ref={titleRef}
          $renameAllowed={renameAllowed(store.column.id)}
          onDoubleClick={store.groupStartEdit}
        >
          {state.visibleColumnTitle}
          <div className="inline-grid items-center align-middle">
            {type === 'basic' && showCounterInColumns && (
              <>
                <Tooltip content={Lang.context.board.counterInColumnsHint1}>
                  <Badge
                    type="rounded"
                    size={16}
                    className="inline-flex ml-4 w-fit row-start-1 col-start-1 row-end-2 col-end-2"
                  >
                    {count}
                  </Badge>
                </Tooltip>
              </>
            )}
            <ApiUi category="column-header-after-title" id={column.id} />
          </div>
        </TwTaskGroupTitleText>
      )}
    </Tooltip>
  )
})
